import { FormRoot, FormInput, FormSelect, FormTextarea, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";


import { PackageIcon } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import type { CreateProductPayload } from "@/modules/catalog/domain/types";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import {
  PRODUCT_IMAGE_KEY_PREFIX,
  ProductImageUploadField,
} from "./ProductImageUploadField";

const ProductFormSchema = z.object({
  type: z.enum(["MENU", "RETAIL"]),
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ສິນຄ້າ"),
  description: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  isActive: z.boolean(),
});

export type ProductFormValues = z.infer<typeof ProductFormSchema>;

type ProductFormProps = {
  defaultValues?: Partial<ProductFormValues>;
  onSubmit: (values: CreateProductPayload) => Promise<void>;
  submitting: boolean;
  onCancel: () => void;
  submitLabel?: string;
};

export function ProductForm({
  defaultValues,
  onSubmit,
  submitting,
  onCancel,
  submitLabel = "ບັນທຶກ",
}: ProductFormProps) {
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const methods = RHF.useForm<ProductFormValues>({
    resolver: zodResolver(ProductFormSchema),
    defaultValues: {
      type: "MENU",
      name: "",
      description: null,
      imageUrl: null,
      isActive: true,
      ...defaultValues,
    },
  });

  return (
    <FormRoot<ProductFormValues>
      methods={methods}
      onSubmit={async (values) => {
        let imageUrl = values.imageUrl ?? null;
        if (pendingImageFile) {
          imageUrl = await uploadImageToKey(
            PRODUCT_IMAGE_KEY_PREFIX,
            pendingImageFile,
          );
          setPendingImageFile(null);
        }
        await onSubmit({
          type: values.type,
          name: values.name,
          description: values.description ?? null,
          imageUrl,
          isActive: values.isActive,
        });
      }}
      className="space-y-6"
    >
      <Card className="rounded-xl border bg-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 font-heading text-lg">
            <PackageIcon className="h-5 w-5 text-muted-foreground" />
            ຂໍ້ມູນສິນຄ້າ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* บล็อกรูป + ข้อมูลหลัก: รูปซ้าย ฟอร์มขวา */}
          <div className="grid gap-6 sm:grid-cols-[minmax(0,240px)_1fr]">
            <div className="flex flex-col gap-2">
              <RHF.Controller
                name="imageUrl"
                control={methods.control}
                render={({ field }) => (
                  <ProductImageUploadField
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    pendingFile={pendingImageFile}
                    onFileSelect={setPendingImageFile}
                    disabled={submitting}
                    label="ຮູບສິນຄ້າ"
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-4">
              <FormSelect
                label="ປະເພດ"
                name="type"
                options={[
                  { label: "MENU (ເມນູເຄື່ອງດື່ມ)", value: "MENU" },
                  { label: "RETAIL (ສິນຄ້າສຳເລັດຮູບ)", value: "RETAIL" },
                ]}
                requiredMark
              />
              <FormInput
                label="ຊື່ສິນຄ້າ"
                name="name"
                placeholder="ຕົວຢ່າງ: Americano"
                className="rounded-[12px]"
                requiredMark
              />
            </div>
          </div>

          <FormTextarea
            label="ຄຳອະທິບາຍ"
            name="description"
            placeholder="ຄຳອະທິບາຍສັ້ນໆ (optional)"
            className="min-h-24 resize-none rounded-[12px]"
          />

          {/* สถานะขาย: แยกส่วนชัด */}
          <div className="rounded-[12px] border bg-muted/30 p-4">
            <RHF.Controller
              name="isActive"
              control={methods.control}
              render={({ field }) => (
                <label
                  htmlFor="product-is-active"
                  className="flex cursor-pointer items-center gap-3"
                >
                  <Checkbox
                    id="product-is-active"
                    checked={field.value}
                    onCheckedChange={(v) => field.onChange(v === true)}
                  />
                  <div>
                    <span className="font-medium text-sm">ເປີດຂາຍ</span>
                    <p className="text-muted-foreground text-xs">
                      ສິນຄ້າຈະເປີດໃຫ້ລູກຄ້າເລືອກໄດ້ໃນເມນູ
                    </p>
                  </div>
                </label>
              )}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          ຍົກເລີກ
        </Button>
        <Button type="submit" isLoading={submitting}>
          {submitLabel}
        </Button>
      </div>
    </FormRoot>
  );
}
